/** @format */
import { TFunction } from 'i18next';
import React from 'react';

import Heart from '@toucan/client/global/icons/heart-outline.svg';
import AccountSvg from '@toucan/client/global/icons/legacy/account.svg';
import SellSvg from '@toucan/client/global/icons/legacy/sell.svg';
import TransactionsSvg from '@toucan/client/global/icons/legacy/transaction.svg';
import PoundStirlingSVG from '@toucan/client/global/icons/pound-sterling.svg';

import { SidebarNavLink } from '@toucan/client/features/layout/components/account-layout/components/sidebar-navigation';

/**
 * Gets the navigation object required for the SidebarNavigation component
 * in the account section
 */
export const getAccountNavigation = (
    // eslint-disable-next-line id-length -- Legacy code: do not use as an example.
    t: TFunction,
    marketSlug: string,
    isSellBalanceEnabled: boolean
): SidebarNavLink[] => {
    const accountSlug = t('account', { ns: 'urls' });

    const accountNavigation = [
        {
            content: t('Profile and settings'),
            icon: <AccountSvg />,
            children: [
                {
                    content: t('Personal details'),
                    href: '/account/personal-details',
                    as: `/${marketSlug}/${accountSlug}/${t('personal-details', {
                        ns: 'urls',
                    })}`,
                    'data-testid': 'account-nav__personal-details',
                },
                {
                    content: t('Address book'),
                    href: '/account/address-book',
                    as: `/${marketSlug}/${accountSlug}/${t('address-book', {
                        ns: 'urls',
                    })}`,
                    'data-testid': 'account-nav__address-book',
                },
                {
                    content: t('Phone numbers'),
                    href: '/account/phone-numbers',
                    as: `/${marketSlug}/${accountSlug}/${t('phone-numbers', {
                        ns: 'urls',
                    })}`,
                    'data-testid': 'account-nav__phone-numbers',
                },
                {
                    content: t('Change password'),
                    href: '/account/change-password',
                    as: `/${marketSlug}/${accountSlug}/${t('change-password', {
                        ns: 'urls',
                    })}`,
                    'data-testid': 'account-nav__change-password',
                },
                {
                    content: t('Marketing preferences'),
                    href: '/account/marketing-preferences',
                    as: `/${marketSlug}/${accountSlug}/${t(
                        'marketing-preferences',
                        {
                            ns: 'urls',
                        }
                    )}`,
                    'data-testid': 'account-nav__marketing-preferences',
                },
            ],
            'data-testid': 'account-nav__profile-and-settings',
        },
        {
            content: t('Quotes'),
            icon: <SellSvg />,
            children: [
                {
                    content: t('Open quotes'),
                    href: '/account/open-quotes',
                    as: `/${marketSlug}/${accountSlug}/${t('open-quotes', {
                        ns: 'urls',
                    })}`,
                    'data-testid': 'account-nav__open-quotes',
                },
                {
                    content: t('Expired quotes'),
                    href: '/account/expired-quotes',
                    as: `/${marketSlug}/${accountSlug}/${t('expired-quotes', {
                        ns: 'urls',
                    })}`,
                    'data-testid': 'account-nav__expired-quotes',
                },
            ],
            'data-testid': 'account-nav__quotes',
        },
        {
            content: t('Transactions'),
            icon: <TransactionsSvg />,
            href: '/account/transactions',
            as: `/${marketSlug}/${accountSlug}/${t('transactions', {
                ns: 'urls',
            })}`,
            'data-testid': 'account-nav__transactions',
        },
        {
            content: t('Saved'),
            icon: <Heart />,
            children: [
                {
                    content: t('Saved models'),
                    href: '/account/saved-models',
                    as: `/${marketSlug}/${accountSlug}/${t('saved-models', {
                        ns: 'urls',
                    })}`,
                    'data-testid': 'account-nav__saved-models',
                },
                {
                    content: t('Saved products'),
                    href: '/account/saved-products',
                    as: `/${marketSlug}/${accountSlug}/${t('saved-products', {
                        ns: 'urls',
                    })}`,
                    'data-testid': 'account-nav__saved-products',
                },
            ],
            'data-testid': 'account-nav__saved',
        },
    ];

    if (isSellBalanceEnabled) {
        accountNavigation.unshift({
            content: t('MPB balance'),
            icon: <PoundStirlingSVG />,
            href: '/account/balance',
            as: `/${marketSlug}/${accountSlug}/${t('balance', {
                ns: 'urls',
            })}`,
            'data-testid': 'account-nav__balance',
        });
    }

    return accountNavigation;
};
